import React, {
	useCallback,
	useEffect,
	useMemo,
} from 'react';
import { ColumnsType } from 'antd/lib/table/interface';
import { faInbox } from '@fortawesome/pro-thin-svg-icons';
import { Moment } from 'moment';
import {
	Space,
	Tag,
} from 'antd';
import Button from 'antd/lib/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
	CargoUnitLabels,
	DATE_AND_TIME,
	CargoUnitTypes,
	Currencies,
	QuantityOptions,
	FreightRateType,
	LaytimeTerms,
	LaytimeIntervals,
	EstimateStatus,
	FixtureCounterpartyTypes,
	LaytimeCalculationMethods,
	CargoContractStatusTypes,
} from '@shared/utils/constants';
import { standardSort } from '@shared/utils/standardSort';
import { Values } from '@shared/utils/objectEnums';
import { formatCurrency } from '@shared/utils/currency';
import type { Port } from '@api/utils/ports';
import type { GetCargosResponse } from '@api/features/cargos/getCargos';
import type {
	EstimatedExpenseItem,
	EstimatedRevenueItem,
} from '@api/models/cargo';
import { CargoDetails } from '@api/features/cargos/getCargoDetails';
import { formatDate } from '@client/utils/formatDate';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import {
	copyCargo,
	createCargo,
	getCargos,
	getCounterparties,
	getUserData,
} from '@client/lib/api';
import ListTableScreen from '@client/components/screens/ListTableScreen';
import showErrorNotification from '@client/utils/showErrorNotification';
import { Links } from '@client/utils/links';
import { getFilterProps } from '@client/utils/table';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import { useTableQuery } from '@client/utils/hooks/useTableQuery';
import CargoContractStatusTag from '@client/screens/estimates/details/components/CargoContractStatusTag';
import { useAuth } from '@client/lib/auth';
import getRelevantCargoTypes from '../details/helpers/getRelevantCargoTypes';
import styles from './CargosListScreen.module.css';

export type CargoPort = {
	id?: number;
	portAndActionKey: string;
	amountLoaded: number;
	loadingRate: number;
	laycanFrom: Moment;
	laycanTo: Moment;
	demurrage: number;
	despatch: number;
	cpTimeAllowed: number;
	calculationMethod: LaytimeCalculationMethods;
	laytimeInterval: LaytimeIntervals;
};

export type CargoFormValues = {
	charterer: number | null;
	estimatedItems: Array<EstimatedRevenueItem | EstimatedExpenseItem>;
	deadfreight: number | null;
	type: string;
	dischargePorts: number[];
	freightRate: {
		currency: Values<typeof Currencies>;
		value: number;
		exchangeRate: number;
	};
	freightType: Values<typeof FreightRateType>;
	loadingPorts: number[];
	overage: number | null;
	quantity: {
		quantity: number;
		unit: Values<typeof CargoUnitTypes>;
	};
	quantityOption: QuantityOptions | null;
	tolerance: number | null;
	laytimeTerms: LaytimeTerms | null;
	status: EstimateStatus | null;
	demurrage: number | null;
	despatch: number | null;
	demurrageLoading: number | null;
	despatchLoading: number | null;
	demurrageDischarge: number | null;
	despatchDischarge: number | null;
};

type CreateCargoFormData = {
	type: string;
	charterer?: number;
	estimateId?: number;
};

const CargosListScreen = () => {
	const {
		data: cargos,
		isLoading,
		error,
		refetch,
		pagination,
		setSearch,
		onTableChange,
	} = useTableQuery<GetCargosResponse>({
		queryKey: ['cargos'],
		fetcher: ({ pagination, filters, sorter, search }) =>
			getCargos({
				pagination,
				sorter,
				search,
				filters,
				includeGhostCargos: true,
				includeVcInCargos: false,
			}),
		defaultPageSize: 100,
	});

	const { userOptions } = useAuth();
	const [userData] = useFetchedState(getUserData);

	const relevantCargoTypes = useMemo(
		() => getRelevantCargoTypes(userData, null),
		[userData],
	);

	const fixedByOptions = useMemo(() => (
		(userOptions ?? [])
			.map((user) => ({
				fixedByName: user.label,
			}))
	), [userOptions]);

	useEffect(() => {
		refetch();
	}, []);

	const [charterers, _reloadCharterers, _error, _loading] = useFetchedState(
		() => getCounterparties(FixtureCounterpartyTypes.CHARTEREROWNER),
	);

	const chartererOptions = useMemo(() => {
		if (charterers == null) {
			return [];
		}

		return charterers.map((c) => ({
			chartererName: c.name,
		}));
	}, [charterers]);

	const columns: ColumnsType<CargoDetails> = useMemo(() => [
		{
			title: 'Id',
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: 'Contract status',
			key: 'contractStatus',
			...getFilterProps(
				Object.values(CargoContractStatusTypes),
				'select',
				'contractStatus',
				'asc'
			),
			render: (_, row: CargoDetails) => (
				<CargoContractStatusTag
					status={row.contractStatus}
				/>
			),
		},
		{
			title: 'Loadport(s)',
			dataIndex: 'loadingPorts',
			key: 'loadingPorts',
			render: (loadPorts: Port[]) => (loadPorts.length > 0 ?
				(
					<Space>
						{(loadPorts ?? []).map((port: Port) => (
							<Tag color="geekblue">{port.name}</Tag>
						))}
					</Space>
				) : 'N/A'),
			width: 100,
		},
		{
			title: 'Disports(s)',
			dataIndex: 'dischargePorts',
			key: 'dischargePorts',
			render: (disports: Port[]) => (disports.length > 0 ?
				(
					<Space>
						{(disports ?? []).map((port: Port) => (
							<Tag color="red">{port.name}</Tag>
						))}
					</Space>
				) : 'N/A'),
			width: 100,
		},
		{
			title: 'Commodity',
			dataIndex: 'type',
			key: 'type',
			sorter: standardSort('type'),
			width: 100,
		},
		{
			title: 'Laydays',
			dataIndex: 'firstLaycan',
			key: 'firstLaycan',
			sorter: standardSort('firstLaycan'),
			render: (date) => (date != null ? formatDate(date, DATE_AND_TIME) : 'Not specified'),
		},
		{
			title: 'Cancelling',
			dataIndex: 'lastLaycan',
			key: 'lastLaycan',
			sorter: standardSort('lastLaycan'),
			render: (date) => (date != null ? formatDate(date, DATE_AND_TIME) : 'Not specified'),
		},
		{
			title: 'Type',
			dataIndex: 'type',
			key: 'type',
			sorter: standardSort('type'),
		},
		{
			title: 'Relet',
			dataIndex: 'hasRelet',
			key: 'hasRelet',
			width: 80,
			...getFilterProps(
				[
					{ hasRelet: 'Yes' },
					{ hasRelet: 'No' },
				],
				'select',
				'hasRelet',
				'asc',
			),
			sorter: standardSort('hasRelet'),
			render: (hasRelet: 'Yes' | 'No') => (
				<Tag color={hasRelet === 'Yes' ? 'green' : 'default'}>
					{hasRelet === 'Yes' ? 'Yes' : 'No'}
				</Tag>
			),
		},
		{
			title: 'Quantity',
			dataIndex: 'quantity',
			key: 'quantity',
			sorter: standardSort('quantity'),
			render: (quantity, row) => (quantity != null ? `${quantity} ${CargoUnitLabels[row.unit]}` : 'N/A'),
		},
		{
			title: 'Freight',
			dataIndex: 'freightType',
			key: 'freightType',
			render: (freightType, row) => {
				if (freightType == null || row.freightRate == null || row.currency == null) {
					return 'N/A';
				}

				return freightType === FreightRateType.LUMPSUM ?
					formatCurrency(row.freightRate, row.currency) :
					`${formatCurrency(row.freightRate, row.currency)} /${row.unit}`;
			},
		},
		{
			title: 'Charterer',
			dataIndex: 'chartererName',
			render: (chartererName) => chartererName ?? 'N/A',
			...getFilterProps(chartererOptions, 'select', 'chartererName', 'desc'),
		},
		{
			title: 'Created/Modified',
			dataIndex: 'updatedAt',
			key: 'updatedAt',
			sorter: standardSort('updatedAt'),
			defaultSortOrder: 'descend',
			render: (date) => (date != null ? formatDate(date, DATE_AND_TIME) : 'N/A'),
		},
		{
			title: 'Fixed By',
			dataIndex: 'fixedByName',
			key: 'fixedByName',
			...getFilterProps(
				fixedByOptions,
				'select',
				'fixedByName',
				'asc'
			),
			sorter: standardSort('fixedByName'),
		},
		{
			title: 'Action',
			align: 'center',
			render: (_, row) => (
				<Button
					type="link"
					onClick={async (e: any) => {
						e.stopPropagation();
						await copyCargo(row.id, false);
						showSuccessNotification(
							'Cargo copied',
							`The cargo ${row.type} was successfully copied`,
							4,
						);
						await refetch();
					}}
					icon={(
						<FontAwesomeIcon
							icon={faCopy as IconProp}
						/>
					)}
				/>
			),
			width: 40,
		},
	], [refetch, chartererOptions]);

	const createNewCargo = useCallback(async (values: {
		type: string;
		charterer?: number;
	}) => {
		try {
			return await createCargo({
				attributes: values,
			});
		} catch (e) {
			showErrorNotification('Could not create cargo', e as Error);

			return null;
		}
	}, []);
	
	const formFields = useMemo(() => [
		{
			label: 'Commodity',
			name: 'type',
			type: 'autocomplete',
			required: true,
			options: relevantCargoTypes.map((cargoType) => ({
				label: cargoType.name,
				value: cargoType.name,
			})),
			inputProps: {
				placeholder: 'Select or enter commodity',
				filterOption: (
					inputValue: string,
					option?: { label?: React.ReactNode },
				) => (
					typeof option?.label === 'string' &&
			option.label
				.toLowerCase()
				.includes(inputValue.toLowerCase())
				),
			},
		},
		{
			label: 'Charterer',
			name: 'charterer',
			type: 'select',
			required: false,
			options: charterers
				?.slice()
				.sort((a, b) => (
					a.name.localeCompare(
						b.name,
						undefined,
						{ sensitivity: 'base' },
					)
				))
				.map((charterer) => ({
					label: charterer.name,
					value: charterer.id,
				})),
			inputProps: {
				showSearch: true,
				placeholder: 'Select charterer',
			},
		},
	], [
		charterers,
		relevantCargoTypes,
	]);

	return (
		<ListTableScreen<CargoDetails, CreateCargoFormData>
			title="Cargos"
			rootPageTitle="Cargos"
			columns={columns}
			loading={isLoading}
			emptyTitle="No cargos found"
			emptyIcon={faInbox}
			tableProps={{
				size: 'small',
				pagination: {
					current: pagination.current,
					pageSize: pagination.pageSize,
					total: cargos?.count ?? 0,
					pageSizeOptions: [50, 100, 200, 500],
				},
				onChange: onTableChange,
				scroll: { x: 1350 },
				rowClassName: (row: CargoDetails) => (
					row.contractStatus === CargoContractStatusTypes.Cancelled ?
						styles.cancelledCargoRow : ''
				),
			}}
			error={error as Error}
			data={cargos?.rows}
			// @ts-ignore
			formFields={formFields}
			formButtonText="Create cargo"
			onFormSubmit={createNewCargo}
			showEmptyArrow
			getRowHref={(row) => Links.Cargo.get(row.id)}
			showSearch
			onSearch={setSearch}
		/>
	);
};

export default CargosListScreen;
